/**
 * Three-way field diff for the optimistic-concurrency ("This record changed")
 * conflict dialog.
 *
 * At conflict time the save saga holds three versions of the record:
 * - `base`   — the record as the user loaded it (the cached copy before the save),
 * - `mine`   — the variables the user tried to save,
 * - `theirs` — the server's current record, carried in the 409 body.
 *
 * `computeStaleWriteDiff` lists every field whose value differs between `mine`
 * and `theirs`, and says who changed it relative to `base`. That tells the user
 * whether the concurrent change overlaps their own edits, and it is the input
 * for per-field resolution ("keep mine" / "take theirs" / "edit").
 *
 * Custom-object `value` blobs are expanded one level, so each attribute is its
 * own row (`value.<attr>`), matching how the record form shows them.
 *
 * operator-task-fe-occ-conflict-dialog-show-diff-and-per-field-resolution
 */

export type FieldChangeOrigin = "mine" | "theirs" | "both";

export interface StaleWriteFieldDiff {
    /** Field path, e.g. "description" or "value.status". */
    path: string;
    /** Top-level field ("value" for an expanded attribute). */
    field: string;
    /** Attribute key inside `value`, when the row is an expanded attribute. */
    attribute?: string;
    base: unknown;
    mine: unknown;
    theirs: unknown;
    /** Who changed the field relative to `base`. "both" = a true overlap. */
    changedBy: FieldChangeOrigin;
}

type RecordLike = Record<string, unknown> | null | undefined;

/** Save-transport keys that are not record content and never shown in the diff. */
export const STALE_WRITE_IGNORED_FIELDS: ReadonlySet<string> = new Set([
    "id",
    "by",
    "expected_version",
    "record_version",
    "modified_at",
    "modified_by",
    "created_at",
    "created_by",
    "migrate_deprecations",
]);

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
    v !== null && typeof v === "object" && !Array.isArray(v);

/** Structural equality for JSON-like values (key order is ignored). */
export const jsonEqual = (a: unknown, b: unknown): boolean => {
    if (a === b) {
        return true;
    }
    // Treat null and undefined as the same "empty" value: a save payload omits
    // a cleared field where the server record carries null.
    if (a == null && b == null) {
        return true;
    }
    if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((item, i) => jsonEqual(item, b[i]));
    }
    if (isPlainObject(a) && isPlainObject(b)) {
        const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
        for (const key of keys) {
            if (!jsonEqual(a[key], b[key])) {
                return false;
            }
        }
        return true;
    }
    return false;
};

const origin = (base: unknown, mine: unknown, theirs: unknown): FieldChangeOrigin => {
    const iChanged = !jsonEqual(base, mine);
    const theyChanged = !jsonEqual(base, theirs);
    if (iChanged && theyChanged) {
        return "both";
    }
    // mine !== theirs here, so at least one side changed; with no base both read
    // as changed and land in "both" above.
    return iChanged ? "mine" : "theirs";
};

/**
 * Fields differing between `mine` and `theirs`, ordered as they appear in
 * `mine`. Only fields present in `mine` are compared: those are the fields the
 * save would write, so they are the ones an overwrite could clobber or a
 * reload could discard.
 */
export const computeStaleWriteDiff = (
    base: RecordLike,
    mine: RecordLike,
    theirs: RecordLike,
): StaleWriteFieldDiff[] => {
    const rows: StaleWriteFieldDiff[] = [];
    if (!mine) {
        return rows;
    }
    const b = base ?? {};
    const t = theirs ?? {};
    for (const field of Object.keys(mine)) {
        if (STALE_WRITE_IGNORED_FIELDS.has(field)) {
            continue;
        }
        const mineValue = mine[field];
        const theirValue = t[field];
        const baseValue = b[field];
        if (field === "value" && isPlainObject(mineValue) && isPlainObject(theirValue)) {
            const baseObj = isPlainObject(baseValue) ? baseValue : {};
            const attrs = new Set([...Object.keys(mineValue), ...Object.keys(theirValue)]);
            for (const attribute of attrs) {
                const m = mineValue[attribute];
                const th = theirValue[attribute];
                if (jsonEqual(m, th)) {
                    continue;
                }
                const bs = baseObj[attribute];
                rows.push({
                    path: `value.${attribute}`,
                    field,
                    attribute,
                    base: bs,
                    mine: m,
                    theirs: th,
                    changedBy: origin(bs, m, th),
                });
            }
            continue;
        }
        if (jsonEqual(mineValue, theirValue)) {
            continue;
        }
        rows.push({
            path: field,
            field,
            base: baseValue,
            mine: mineValue,
            theirs: theirValue,
            changedBy: origin(baseValue, mineValue, theirValue),
        });
    }
    return rows;
};

export type FieldResolution = "mine" | "theirs" | { edited: unknown };

/**
 * Build the variables to re-save after the user resolved each differing field.
 * Starts from `mine`; a field resolved to "theirs" takes the server value, an
 * edited field takes the edited value. Fields the user did not touch and the
 * other side changed take the server value too (absent a resolution entry, a
 * "theirs"-only change defaults to theirs), so resolving never clobbers a
 * concurrent change the user did not see.
 */
export const applyStaleWriteResolutions = (
    mine: Record<string, unknown>,
    diff: StaleWriteFieldDiff[],
    resolutions: Record<string, FieldResolution>,
): Record<string, unknown> => {
    const result: Record<string, unknown> = { ...mine };
    if (isPlainObject(mine.value)) {
        result.value = { ...mine.value };
    }
    for (const row of diff) {
        const choice = resolutions[row.path] ?? (row.changedBy === "theirs" ? "theirs" : "mine");
        if (choice === "mine") {
            continue;
        }
        const next = choice === "theirs" ? row.theirs : choice.edited;
        if (row.attribute !== undefined && isPlainObject(result.value)) {
            const value = result.value as Record<string, unknown>;
            if (next === undefined) {
                delete value[row.attribute];
            } else {
                value[row.attribute] = next;
            }
        } else {
            result[row.field] = next;
        }
    }
    return result;
};
